import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import '../App.css';
import SearchableSelect from './SearchableSelect';
import HowPacketsDrawer from './HowPacketsDrawer';
import RunOfShowPanel, { RosTable, RosSection, SharedDetailsForm, ManualEventForm, GenerateGate, gateAttention, tripText, teamText } from './RunOfShowPanel';
import { newActivityForm, activityFormFrom, activityPayload, removalLabel, removalConfirmText, pendingActivityRow } from '../ros/activities';
import { sortRosEvents } from '../ros/rosActivity';
import { savedLocations } from '../ros/locations';
import { SharedProductionSection, Collapse, productionSummaries, formatMB } from './MultiEventProduction';
import MultiEventPicker from './MultiEventPicker';
import { fetchDeliveryJson, isLikelyFetchDropError } from '../utils/deliveryApiFetch';
import { PACKET_VIEW_KEY, readView, writeView } from '../utils/viewMemory';
import { formatDisplayInstant } from '../utils/displayDate';

function projectSelectionKey(project) {
  return [
    project.opportunityId || '',
    project.loadInDateISO || '',
    project.gdriveFolderId || ''
  ].join('||');
}

// Canonical EPS event identity (survives row inserts on Active). epsRowNumber is
// only a hint for faster lookups; the script re-validates and falls back to
// Copper ID + load-in + folder if the hint is stale.
function packetIdentityFields(project) {
  return {
    opportunityId: project.opportunityId,
    loadInDateISO: project.loadInDateISO || '',
    gdriveFolderId: project.gdriveFolderId || '',
    disambiguate: '1',
    epsRowNumber: project.epsRowNumber ? String(project.epsRowNumber) : ''
  };
}

// Apps Script answers with a redirect to a temporary page. That page sometimes
// comes back as Google Drive's HTML "Page Not Found" instead of JSON. One
// retry usually gets the real response.
const UPCOMING_PROJECTS_CACHE_KEY = 'pp_upcoming_projects_v1';
const UPCOMING_PROJECTS_CACHE_TS = 'pp_upcoming_projects_ts_v1';
const UPCOMING_PROJECTS_TTL_MS = 3 * 60 * 1000;

function readCachedUpcomingProjects() {
  try {
    const ts = Number(sessionStorage.getItem(UPCOMING_PROJECTS_CACHE_TS) || '0');
    if (!ts || Date.now() - ts > UPCOMING_PROJECTS_TTL_MS) return null;
    const raw = sessionStorage.getItem(UPCOMING_PROJECTS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
}

function writeCachedUpcomingProjects(projects) {
  try {
    sessionStorage.setItem(UPCOMING_PROJECTS_CACHE_KEY, JSON.stringify(projects || []));
    sessionStorage.setItem(UPCOMING_PROJECTS_CACHE_TS, String(Date.now()));
  } catch (_) {
    /* quota */
  }
}

function clearCachedUpcomingProjects() {
  try {
    sessionStorage.removeItem(UPCOMING_PROJECTS_CACHE_KEY);
    sessionStorage.removeItem(UPCOMING_PROJECTS_CACHE_TS);
  } catch (_) {
    /* ignore */
  }
}

function fetchPacketJson(url, options) {
  return fetchDeliveryJson(url, options, { retries: 2 });
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Whether this backend has the Multi-Event packet set up (cheap check, cached
// for the session so the landing page does not flicker between layouts).
const ROS_STATUS_CACHE_KEY = 'pp_ros_status_v1';

function readCachedRosStatus() {
  try {
    const v = sessionStorage.getItem(ROS_STATUS_CACHE_KEY);
    return v === '1' ? true : v === '0' ? false : null;
  } catch (_) {
    return null;
  }
}

function writeCachedRosStatus(enabled) {
  try {
    sessionStorage.setItem(ROS_STATUS_CACHE_KEY, enabled ? '1' : '0');
  } catch (_) {
    /* ignore */
  }
}

function identityKey(identity) {
  return identity ? [identity.opportunityId, identity.loadInDateISO, identity.gdriveFolderId].join('||') : '';
}

const GENERATE_POLL_MS = 15000;
const GENERATE_POLL_MAX_MS = 6 * 60 * 1000;

function ProductionPackets({ deliveryApiUrl = '', userEmail = '' }) {
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState('');

  // The packet open before a page refresh, if any: { flow: 'standard', projectKey } or { flow: 'multi', params }.
  const [restoredView] = useState(() => readView(PACKET_VIEW_KEY));
  const [selectedProjectKey, setSelectedProjectKey] = useState(
    () => (restoredView && restoredView.flow === 'standard' && restoredView.projectKey) || ''
  );
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [eps, setEps] = useState(null);
  const [stale, setStale] = useState(null);
  // The 72-hour production gate for this project, as the server last worked it out.
  const [gate, setGate] = useState(null);

  const [form, setForm] = useState(emptyForm());
  const [versionInfo, setVersionInfo] = useState(emptyVersion());

  const [generating, setGenerating] = useState(false);
  const [generateStatus, setGenerateStatus] = useState('');
  const [autosaveStatus, setAutosaveStatus] = useState(''); // '', 'saving', 'saved', 'error'
  const [howOpen, setHowOpen] = useState(false);

  // Packet type is chosen first: '' (not yet), 'standard' or 'multi'.
  // Multi-Event: 'pick' projects, then 'edit' the packet (new or existing).
  const [rosStatus, setRosStatus] = useState(readCachedRosStatus);
  const [flow, setFlow] = useState(() => (restoredView && restoredView.flow) || '');
  const [rosSummary, setRosSummary] = useState(null);
  const [multiStage, setMultiStage] = useState('pick');
  const [multiSelection, setMultiSelection] = useState([]);
  const [multiCtx, setMultiCtx] = useState(null);
  const [multiError, setMultiError] = useState('');
  const [multiLoading, setMultiLoading] = useState(false);

  // A Standard project's Run of Show rows and Production Information, built by
  // the same backend functions the Multi-Event builder uses for each project.
  const [projectView, setProjectView] = useState(null);
  const [projectViewLoading, setProjectViewLoading] = useState(false);
  const [projectViewError, setProjectViewError] = useState('');
  const viewRequest = useRef(0);
  // The departure time the shown Run of Show was built with.
  const viewDeparture = useRef('');
  const [openParts, setOpenParts] = useState({});
  const [showReview, setShowReview] = useState(false);

  const apiConfigured = !!deliveryApiUrl;

  // Close the help drawer on Escape without affecting the rest of the form.
  useEffect(() => {
    if (!howOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setHowOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [howOpen]);

  // Refs for the advisory fields so "Review Missing Fields" can scroll to the first gap.
  const refOriginDeparture = useRef(null);
  const refCrew = useRef(null);
  const refTrip = useRef(null);
  const latestPacketRef = useRef(null);
  // Autosave bookkeeping: skip the save that would otherwise fire right after a load.
  const autosave = useRef({ skip: true, timer: null });

  const fetchProjects = useCallback(async (opts = {}) => {
    if (!apiConfigured) return;
    const force = !!opts.force;
    const cached = !force ? readCachedUpcomingProjects() : null;
    if (cached) {
      setProjects(cached);
      setLoadingProjects(false);
      return;
    }
    setLoadingProjects(true);
    setProjectsError('');
    try {
      const data = await fetchPacketJson(`${deliveryApiUrl}?action=getUpcomingProjects`);
      if (data.success) {
        const list = data.projects || [];
        setProjects(list);
        writeCachedUpcomingProjects(list);
      } else {
        setProjectsError(data.error || 'Failed to load projects');
      }
    } catch (err) {
      setProjectsError(err.message || 'Network error');
    } finally {
      setLoadingProjects(false);
    }
  }, [deliveryApiUrl, apiConfigured]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (!apiConfigured) return undefined;
    let live = true;
    (async () => {
      try {
        const data = await fetchPacketJson(`${deliveryApiUrl}?action=getRosStatus`);
        const enabled = !!(data && data.success && data.rosEnabled);
        if (live) setRosStatus(enabled);
        writeCachedRosStatus(enabled);
      } catch (_) {
        if (live) setRosStatus((s) => (s === null ? false : s));
      }
    })();
    return () => { live = false; };
  }, [deliveryApiUrl, apiConfigured]);

  const loadProjectDetails = useCallback(async (project) => {
    if (!apiConfigured || !project || !project.opportunityId) return;
    setLoadingDetails(true);
    setDetailsError('');
    setGenerateStatus('');
    setAutosaveStatus('');
    try {
      const params = new URLSearchParams({
        action: 'getPacketDetails',
        ...packetIdentityFields(project)
      });
      const data = await fetchPacketJson(`${deliveryApiUrl}?${params.toString()}`);
      if (!data.success) {
        setDetailsError(data.error || 'Failed to load packet details');
        return;
      }
      const epsData = data.eps || null;
      setEps(epsData);
      setStale(data.stale || null);
      setGate(data.gate || null);
      const d = data.details || {};
      const e = epsData || {};
      // Loading (re)seeds the form — don't let this trigger an autosave.
      autosave.current.skip = true;
      viewDeparture.current = d.originDepartureTime || '';
      setForm({
        originDepartureTime: d.originDepartureTime || '',
        hotelName: d.hotelName || '',
        numberOfRooms: d.numberOfRooms || '',
        // Imported EPS values seed the editable fields; a saved override wins.
        hotelAddress: d.hotelAddress || e.teamHotelAddress || '',
        teamHotelConf: d.teamHotelConf || e.teamHotelConf || '',
        supervisor: d.supervisor || e.loadInSupervisor || e.loadOutSupervisor || '',
        deliveryPersonnel: Array.isArray(d.deliveryPersonnel) ? d.deliveryPersonnel : [],
        overnightTravelRequired: d.overnightTravelRequired || '',
        teamStructure: d.teamStructure || ''
      });
      setVersionInfo({
        latestVersion: d.latestVersion || 0,
        latestPacketUrl: d.latestPacketUrl || '',
        latestPacketGeneratedAt: d.latestPacketGeneratedAt || '',
        latestPacketGeneratedBy: d.latestPacketGeneratedBy || '',
        formLastSavedAt: d.formLastSavedAt || '',
        formLastSavedBy: d.formLastSavedBy || ''
      });
    } catch (err) {
      setDetailsError(err.message || 'Network error');
    } finally {
      setLoadingDetails(false);
    }
  }, [deliveryApiUrl, apiConfigured]);

  const loadProjectView = useCallback(async (project) => {
    if (!apiConfigured || !project || !project.opportunityId) return;
    const req = ++viewRequest.current;
    setProjectViewLoading(true);
    setProjectViewError('');
    try {
      const params = new URLSearchParams({ action: 'getProjectView', ...packetIdentityFields(project) });
      const data = await fetchPacketJson(`${deliveryApiUrl}?${params.toString()}`);
      if (req !== viewRequest.current) return;
      if (data.success) setProjectView(data);
      else setProjectViewError(data.error || 'Could not read this project’s production details.');
    } catch (err) {
      if (req === viewRequest.current) setProjectViewError(err.message || 'Network error');
    } finally {
      if (req === viewRequest.current) setProjectViewLoading(false);
    }
  }, [deliveryApiUrl, apiConfigured]);

  /* ---------- Standard Run of Show: shared Production Activities + this packet's visibility ---------- */

  const [activityForm, setActivityForm] = useState(null);
  const [activityBusy, setActivityBusy] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [rosMessage, setRosMessage] = useState('');
  const stdActivity = (id) => ((projectView && projectView.ros.activities) || []).find((a) => a.id === id);
  const postStandard = (action, extra) => fetchDeliveryJson(deliveryApiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify({ action, context: 'standard', ...packetIdentityFields(selectedProject), userEmail, ...extra }),
    mode: 'cors'
  }, { retries: 1 });

  // A save or removal replies with this page's Run of Show, so only that part is redrawn.
  const applyRos = (ros) => setProjectView((pv) => (pv ? { ...pv, ros } : pv));

  // The activity shows at once (marked Saving…) in its place; the saved Run of Show replaces it.
  // A failed save takes it out again and reopens the form with the message.
  const saveStandardActivity = async (form) => {
    const payload = activityPayload(form, { memberIds: [projectView.projectId], warehouseRef: projectView.warehouse && projectView.warehouse.ref });
    const pending = pendingActivityRow(form, payload, { warehouse: projectView.warehouse, members: [projectView.member] });
    const replaced = (projectView.ros.events || []).filter((e) => e.key === pending.key);
    setActivityError('');
    setActivityBusy(true);
    setActivityForm(null);
    setProjectView((pv) => (pv ? { ...pv, ros: { ...pv.ros, events: sortRosEvents([...pv.ros.events.filter((e) => e.key !== pending.key), pending]) } } : pv));
    const undo = (message) => {
      setProjectView((pv) => (pv ? { ...pv, ros: { ...pv.ros, events: sortRosEvents([...pv.ros.events.filter((e) => e.key !== pending.key), ...replaced]) } } : pv));
      setActivityForm(form);
      setActivityError(message);
    };
    try {
      const data = await postStandard('saveProductionActivity', { activity: payload, baseUpdatedAt: form.baseUpdatedAt || '' });
      if (data && data.success) {
        if (data.ros) applyRos(data.ros);
        else loadProjectView(selectedProject);
      } else {
        undo((data && data.error) || 'Could not save the activity.');
      }
    } catch (err) {
      undo(`${err.message || 'Could not save the activity.'} Nothing was saved — try again.`);
    } finally {
      setActivityBusy(false);
    }
  };

  const removeStandardActivity = async (id) => {
    const rec = stdActivity(id);
    if (!rec || !window.confirm(removalConfirmText(rec, 'standard'))) return;
    setRosMessage('');
    try {
      const data = await postStandard('removeProductionActivity', { activityId: id, baseUpdatedAt: rec.updatedAt || '' });
      if (data && data.success) {
        if (data.ros) applyRos(data.ros);
        else loadProjectView(selectedProject);
      } else setRosMessage((data && data.error) || 'Could not remove the activity.');
    } catch (err) {
      setRosMessage(err.message || 'Could not remove the activity.');
    }
  };

  // The row changes at once; the saved list (Packet Details) follows, and a failure puts it back.
  const [stdHidden, setStdHidden] = useState(null);
  useEffect(() => { setStdHidden(projectView ? (projectView.ros.hidden || []) : null); }, [projectView]);
  // The screen changes at once and keeps its own list: a save's reply can
  // predate another click, so only a failed save is undone, and only its rows.
  const toggleStandardHidden = async (keys, hidden) => {
    setRosMessage('');
    const set = (on) => (list) => (on ? Array.from(new Set([...(list || []), ...keys])) : (list || []).filter((k) => keys.indexOf(k) === -1));
    setStdHidden(set(hidden));
    try {
      const data = await postStandard('setStandardRowHidden', { keys, hidden });
      if (!(data && data.success)) {
        setStdHidden(set(!hidden));
        setRosMessage((data && data.error) || 'Could not change what this packet shows.');
      }
    } catch (err) {
      setStdHidden(set(!hidden));
      setRosMessage(err.message || 'Could not change what this packet shows.');
    }
  };

  // Ignore a Standard-page lookup that arrives after the user switched projects.
  const rosRequestKey = useRef('');

  // Cheap per-project lookup on the Standard page: the Multi-Event packets
  // this project already belongs to. It never changes the packet type.
  const loadRosSummary = useCallback(async (project) => {
    if (!apiConfigured || !project || !project.opportunityId) return;
    const key = projectSelectionKey(project);
    try {
      const params = new URLSearchParams({ action: 'getRosSummary', ...packetIdentityFields(project) });
      const data = await fetchPacketJson(`${deliveryApiUrl}?${params.toString()}`);
      if (rosRequestKey.current !== key || !data.success) return;
      setRosSummary(data);
    } catch (_) {
      /* the Standard page is unaffected */
    }
  }, [deliveryApiUrl, apiConfigured]);

  // Multi-Event editor context: a new packet from selected Project IDs (the
  // anchor is chosen server-side; nothing is written), or an existing packet.
  const multiRequest = useRef(0);
  const openMultiEditor = useCallback(async (params) => {
    const req = ++multiRequest.current;
    setMultiLoading(true);
    setMultiError('');
    try {
      const q = new URLSearchParams({ action: 'getRosContext', ...params });
      const data = await fetchPacketJson(`${deliveryApiUrl}?${q.toString()}`);
      if (req !== multiRequest.current) return;
      if (data.success && data.anchor && data.anchor.available) {
        setMultiCtx(data);
        setMultiStage('edit');
      } else {
        writeView(PACKET_VIEW_KEY, null);
        setMultiError((data && (data.error || (data.anchor && data.anchor.reason))) || 'Could not open the Multi-Event packet.');
      }
    } catch (err) {
      if (req === multiRequest.current) setMultiError(err.message || 'Network error');
    } finally {
      if (req === multiRequest.current) setMultiLoading(false);
    }
  }, [deliveryApiUrl]);

  const startMulti = (ids) => {
    setMultiSelection(ids);
    openMultiEditor({ members: ids.join(',') });
  };
  const openExistingMulti = (identity) => {
    if (!identity) return;
    setFlow('multi');
    setSelectedProjectKey('');
    openMultiEditor(identity);
  };
  // Reopen the Multi-Event packet that was open before a page refresh.
  useEffect(() => {
    if (restoredView && restoredView.flow === 'multi' && restoredView.params) openMultiEditor(restoredView.params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Remember the open packet for this browser tab: a saved Multi-Event packet by
  // its identity, a new one by its projects, a Standard packet by its project.
  const multiIdentity = multiCtx && multiCtx.rosPacket && multiCtx.anchor ? multiCtx.anchor.packetIdentity : null;
  useEffect(() => {
    if (flow !== 'multi' || multiStage !== 'edit' || !multiCtx) return;
    writeView(PACKET_VIEW_KEY, {
      flow: 'multi',
      params: multiIdentity || { members: multiSelection.join(',') }
    });
  }, [flow, multiStage, multiCtx, multiIdentity, multiSelection]);
  useEffect(() => {
    if (selectedProjectKey) writeView(PACKET_VIEW_KEY, { flow: 'standard', projectKey: selectedProjectKey });
  }, [selectedProjectKey]);

  const resetToDashboard = () => {
    writeView(PACKET_VIEW_KEY, null);
    multiRequest.current++;
    setSelectedProjectKey('');
    setFlow('');
    setMultiStage('pick');
    setMultiSelection([]);
    setMultiCtx(null);
    setMultiError('');
    setMultiLoading(false);
  };

  const selectedProject = useMemo(
    () => projects.find((p) => projectSelectionKey(p) === selectedProjectKey) || null,
    [projects, selectedProjectKey]
  );
  useEffect(() => {
    setRosSummary(null);
    if (selectedProject) {
      rosRequestKey.current = projectSelectionKey(selectedProject);
      loadProjectDetails(selectedProject);
      loadRosSummary(selectedProject);
      setProjectView(null);
      loadProjectView(selectedProject);
    } else if (!selectedProjectKey) {
      viewRequest.current++;
      setProjectView(null);
      setProjectViewError('');
      rosRequestKey.current = '';
      autosave.current.skip = true;
      setEps(null);
      setStale(null);
      setForm(emptyForm());
      setVersionInfo(emptyVersion());
      setAutosaveStatus('');
    }
  }, [selectedProject, selectedProjectKey, loadProjectDetails, loadRosSummary, loadProjectView]);

  const projectOptions = useMemo(() => {
    return projects.map((p) => {
      const dates = p.projectDates || p.loadInDate;
      const parts = [p.clientName, p.venueName, dates].filter(Boolean);
      if (Object.prototype.hasOwnProperty.call(p, 'gdriveFolderId') && !p.gdriveFolderId) {
        parts.push('No Drive folder');
      }
      return { value: projectSelectionKey(p), label: parts.join(' — ') };
    });
  }, [projects]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const openRosMembership = (m) => {
    const anchor = projects.find((p) =>
      String(p.opportunityId || '') === m.anchorCopperId &&
      String(p.loadInDateISO || '') === m.anchorLoadInDate &&
      String(p.gdriveFolderId || '') === m.anchorFolderId
    );
    if (anchor) openExistingMulti(packetIdentityFields(anchor));
    else if (m.latestPacketUrl) window.open(m.latestPacketUrl, '_blank', 'noopener');
  };

  const postForm = useCallback(async () => {
    return fetchPacketJson(deliveryApiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({
        action: 'savePacketDetails',
        ...packetIdentityFields(selectedProject || {}),
        formData: form,
        userEmail: userEmail
      }),
      mode: 'cors'
    });
  }, [deliveryApiUrl, selectedProject, form, userEmail]);

  // Debounced autosave of user-entered packet details. Skips the initial seed after a
  // load and while generating; does NOT reload (to preserve focus/cursor position).
  useEffect(() => {
    if (!apiConfigured || !selectedProject) return undefined;
    const a = autosave.current;
    if (a.skip) {
      a.skip = false;
      return undefined;
    }
    if (generating) return undefined;
    if (a.timer) clearTimeout(a.timer);
    a.timer = setTimeout(async () => {
      setAutosaveStatus('saving');
      try {
        const data = await postForm();
        setAutosaveStatus(data && data.success ? 'saved' : 'error');
        if (data && data.success && data.gate) setGate(data.gate);
        // The Depart Warehouse row shows the saved departure time.
        if (data && data.success && selectedProject && form.originDepartureTime !== viewDeparture.current) {
          viewDeparture.current = form.originDepartureTime;
          loadProjectView(selectedProject);
        }
      } catch (_) {
        setAutosaveStatus('error');
      }
    }, 4000);
    return () => {
      if (a.timer) clearTimeout(a.timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, selectedProject]);

  const applyGenerateSuccess = useCallback((data) => {
    clearCachedUpcomingProjects();
    const version = data.version || data.details?.latestVersion || 0;
    const human = data.generatedAtHuman
      || (data.details?.latestPacketGeneratedAt ? String(data.details.latestPacketGeneratedAt) : '');
    setGenerateStatus(
      `Packet v${version} generated${human ? ` at ${human}` : ''}.`
    );
    setVersionInfo((prev) => ({
      ...prev,
      latestVersion: version,
      latestPacketUrl: data.pdfUrl || data.details?.latestPacketUrl || prev.latestPacketUrl,
      latestPacketGeneratedAt: data.generatedAt || data.details?.latestPacketGeneratedAt || '',
      latestPacketGeneratedBy: userEmail || data.details?.latestPacketGeneratedBy || ''
    }));
    setStale({ isStale: false });
    setTimeout(() => {
      if (latestPacketRef.current) {
        latestPacketRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  }, [userEmail]);

  const pollForNewPacketVersion = useCallback(async (project, versionBefore) => {
    const deadline = Date.now() + GENERATE_POLL_MAX_MS;
    while (Date.now() < deadline) {
      await sleep(GENERATE_POLL_MS);
      try {
        const params = new URLSearchParams({
          action: 'getPacketDetails',
          ...packetIdentityFields(project)
        });
        const data = await fetchPacketJson(`${deliveryApiUrl}?${params.toString()}`);
        const nextVersion = data.details?.latestVersion || 0;
        if (data.success && nextVersion > versionBefore) {
          return {
            success: true,
            version: nextVersion,
            pdfUrl: data.details?.latestPacketUrl,
            details: data.details
          };
        }
      } catch (_) {
        /* keep polling */
      }
    }
    return null;
  }, [deliveryApiUrl]);

  const handleGenerate = async () => {
    if (!apiConfigured || !selectedProject) return;
    if (!eps || !eps.gdriveFolderId) {
      setGenerateStatus('Add a Google Drive folder on the Event Production Sheet before generating a packet.');
      return;
    }
    const versionBefore = versionInfo.latestVersion || 0;
    setGenerating(true);
    setGenerateStatus('Generating packet — keep this tab open (often 2–5 minutes when resources are merged)…');
    try {
      // Never retry generate: a second POST can start duplicate work while the first is still running.
      const data = await fetchDeliveryJson(
        deliveryApiUrl,
        {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify({
            action: 'generatePacket',
            ...packetIdentityFields(selectedProject),
            formData: form,
            userEmail: userEmail
          }),
          mode: 'cors'
        },
        { retries: 1 }
      );
      if (data.success) {
        applyGenerateSuccess(data);
      } else if (data.gate) {
        // Refused by the 72-hour gate: what is missing shows beside the button.
        setGate(data.gate);
        setGenerateStatus('');
      } else {
        setGenerateStatus(`Generation failed: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      if (isLikelyFetchDropError(err)) {
        setGenerateStatus(
          'The browser lost connection while Google was still generating. Checking Drive for a new version…'
        );
        const recovered = await pollForNewPacketVersion(selectedProject, versionBefore);
        if (recovered) {
          applyGenerateSuccess(recovered);
        } else {
          setGenerateStatus(
            'Generation may still be running, or it failed on the server. Wait 2–3 minutes, refresh this page, and check Production Packets → Archive in Drive before generating again.'
          );
        }
      } else {
        setGenerateStatus(`Generation failed: ${err.message || 'Network error'}`);
      }
    } finally {
      setGenerating(false);
    }
  };

  // Advisory readiness, said the way the Multi-Event builder says it: what to
  // review and where. Generation is NOT blocked (incomplete packets are
  // allowed), except without a Drive folder.
  const crewComplete = !!(
    (form.supervisor && form.supervisor.trim()) ||
    form.deliveryPersonnel.some((p) => p.name && p.name.trim())
  );
  const toggleOpenPart = (k) => setOpenParts((o) => ({ ...o, [k]: !o[k] }));
  const gateBlocked = !!(gate && gate.blocked);
  const gateItems = (sec) => (gate && gate.active ? (gate.missing || []).filter((i) => i.section === sec).map((i) => ({ code: i.code, text: i.text })) : []);
  const goToPart = (sec) => {
    setOpenParts((o) => ({ ...o, [sec]: true }));
    const el = sec === 'trip' ? refTrip.current : sec === 'team' ? refCrew.current : null;
    if (el && el.scrollIntoView) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };
  const goTo = (el) => {
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof el.focus === 'function') el.focus();
  };
  const reviewItems = [];
  if (!form.originDepartureTime) {
    reviewItems.push({ text: 'Add the Departure Time — the Depart Warehouse row prints TBD without it.', goLabel: 'Run of Show →', go: () => goTo(refOriginDeparture.current) });
  }
  if (!crewComplete) {
    reviewItems.push({
      text: 'Add a supervisor or delivery personnel.', goLabel: 'Delivery Team →',
      go: () => { setOpenParts((o) => ({ ...o, team: true })); setTimeout(() => goTo(refCrew.current), 50); }
    });
  }
  // The Run of Show's own review items (EPS times still TBD, …); the departure one is above.
  ((projectView && projectView.ros.issues) || [])
    .filter((i) => i.code !== 'departure_time_missing')
    .forEach((i) => reviewItems.push({ text: i.message }));
  const standardSummaries = productionSummaries(projectView && projectView.production, []);
  const changeStandardProject = () => {
    writeView(PACKET_VIEW_KEY, null);
    setSelectedProjectKey('');
  };

  if (!apiConfigured) {
    return (
      <div className="pp-shell">
        <p style={{ color: 'var(--brand-text-light)' }}>
          Production Packets requires REACT_APP_DELIVERY_API_URL to be configured.
        </p>
      </div>
    );
  }

  const projectSelected = !!selectedProject && !!eps && !loadingDetails;
  const missingDriveFolder = !!(projectSelected && !String(eps.gdriveFolderId || '').trim());
  const multiEditing = flow === 'multi' && multiStage === 'edit' && !!multiCtx;
  const inEditor = projectSelected || multiEditing;
  // Packet type comes first once this backend has Multi-Event set up;
  // otherwise the page is exactly the Standard flow.
  const typeFirst = rosStatus === true;
  const projectStepNum = typeFirst ? '02' : '01';
  const memberships = (rosSummary && rosSummary.memberships) || [];

  const standardSelector = (
    <PPSection num={projectStepNum} title="Select Project">
      <>
        <span className="helper-text pp-section-help">
          Choose an upcoming project from the Event Production Sheet.
        </span>
        <div className="pp-selector pp-selector-row">
          <SearchableSelect
            options={projectOptions}
            value={selectedProjectKey}
            onChange={setSelectedProjectKey}
            placeholder={loadingProjects ? 'Loading projects…' : 'Search projects…'}
            loading={loadingProjects}
          />
          <button
            type="button"
            className="pp-refresh-projects-btn"
            disabled={loadingProjects}
            title="Refresh project list from the Event Production Sheet"
            onClick={() => {
              clearCachedUpcomingProjects();
              fetchProjects({ force: true });
            }}
          >
            {loadingProjects ? 'Refreshing…' : 'Refresh list'}
          </button>
        </div>
        {projectsError && (
          <div className="helper-text pp-error">{projectsError}</div>
        )}
        {loadingDetails && (
          <div className="helper-text" style={{ marginTop: 12 }}>Loading project details…</div>
        )}
      </>
    </PPSection>
  );

  return (
    <div className={`pp-shell${inEditor ? ' pp-shell--selected' : ''}`}>
      <div className="pp-pagehead">
        <div className="pp-pagehead-titles">
          <h2 className="tab-headline">Production Packets</h2>
          <p className="calculator-description">
            {rosStatus === true
              ? 'Create a production packet for one project or combine multiple projects with a shared Run of Show.'
              : 'Build production packets for upcoming events.'}
          </p>
        </div>
        <div className="pp-pagehead-actions">
          <button
            type="button"
            className="pp-help-btn"
            aria-haspopup="dialog"
            aria-expanded={howOpen}
            onClick={() => setHowOpen(true)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <line x1="12" y1="10" x2="12" y2="16" />
              <circle cx="12" cy="7" r="0.75" fill="currentColor" stroke="none" />
            </svg>
            How Production Packets Work
          </button>
          {inEditor && (
            <button type="button" className="pp-dashboard-btn" onClick={resetToDashboard}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Production Packet Dashboard
            </button>
          )}
        </div>
      </div>

      {/* 01 — SELECT PACKET TYPE, then 02 — SELECT PROJECT(S); shown until an editor opens */}
      {!inEditor && typeFirst && (
        <PPSection num="01" title="Select Packet Type">
          <PacketTypeSelector
            value={flow}
            onChange={(t) => {
              setFlow(t);
              setMultiError('');
              writeView(PACKET_VIEW_KEY, null);
              if (t !== 'standard') setSelectedProjectKey('');
            }}
          />
        </PPSection>
      )}
      {!inEditor && (!typeFirst || flow === 'standard') && standardSelector}
      {!inEditor && typeFirst && flow === 'multi' && (
        <PPSection num="02" title="Select Projects">
          <MultiEventPicker
            deliveryApiUrl={deliveryApiUrl}
            initialSelection={multiSelection}
            starting={multiLoading}
            startError={multiError}
            onStart={startMulti}
            onOpenPacket={(p) => openExistingMulti(p.identity)}
          />
        </PPSection>
      )}

      {/* Persistent page-level help — available before and after project selection */}
      {howOpen && (
        <HowPacketsDrawer onClose={() => setHowOpen(false)} />
      )}

      {detailsError && !projectSelected && (
        <div className="helper-text pp-error">{detailsError}</div>
      )}

      {multiEditing && (
        <>
          <StepSummary
            num="01"
            title="Packet Type"
            value="Multi-Event Production Packet + Run of Show"
          />
          <StepSummary
            num="02"
            title="Projects"
            value={multiCtx.rosPacket
              ? `${(multiCtx.rosPacket.selectedMembers || []).length} projects — add or remove them under Events Included`
              : `${multiSelection.length} projects selected`}
            action={!multiCtx.rosPacket ? (
              <button type="button" className="pp-link-btn" onClick={() => { writeView(PACKET_VIEW_KEY, null); setMultiStage('pick'); setMultiCtx(null); }}>
                Edit projects
              </button>
            ) : null}
          />
          <PPSection num="03" title="Build Multi-Event Packet">
            <RunOfShowPanel
              key={identityKey(multiCtx.anchor && multiCtx.anchor.packetIdentity)}
              deliveryApiUrl={deliveryApiUrl}
              userEmail={userEmail}
              identity={multiCtx.anchor && multiCtx.anchor.packetIdentity}
              ctx={multiCtx}
              onContext={setMultiCtx}
              embedded
            />
          </PPSection>
        </>
      )}

      {projectSelected && (
        <>
          {missingDriveFolder && (
            <div className="pp-folder-warning" role="status">
              This project has no Google Drive folder on the Event Production Sheet.
              A production packet cannot be created until the GDrive Folder ID is added on that row.
            </div>
          )}
          {/* 01 and 02 read as finished context, as in the Multi-Event builder. */}
          {typeFirst && <StepSummary num="01" title="Packet Type" value="Standard Production Packet" />}
          <StepSummary
            num={projectStepNum}
            title="Project"
            value={standardProjectLine(eps, selectedProject)}
            action={(
              <button type="button" className="pp-link-btn" onClick={changeStandardProject}>Change project</button>
            )}
          />
          {memberships.length > 0 && (
            <div className="ros-membership" role="status">
              {memberships.map((m) => (
                <div key={m.rosPacketId}>
                  Also part of the Multi-Event packet “{m.title}”
                  <span className="pp-action-dot"> · </span>
                  <button type="button" className="pp-link-btn" onClick={() => openRosMembership(m)}>
                    Open →
                  </button>
                </div>
              ))}
            </div>
          )}

          <PPSection num={typeFirst ? '03' : '02'} title="Build Standard Packet" action={<AutosaveIndicator status={autosaveStatus} />}>
            <div className="ros-panel ros-panel--embedded">
              <RosSection title="Run of Show">
                {projectView ? (
                  <RosTable epsUrl={projectView.epsUrl} hideProject
                    // The Depart Warehouse row shows the Origin Departure Time as it is typed (one value, saved with the form); cleared, it is TBD.
                    events={(projectView.ros.events || []).map((e) => {
                      const typed = String(form.originDepartureTime || '').trim();
                      if (!/:departure$/.test(e.key || '') || (!typed && !String(viewDeparture.current || '').trim())) return e;
                      return { ...e, timeLabel: typed || 'TBD' };
                    })}
                    hiddenKeys={stdHidden || projectView.ros.hidden}
                    onHide={projectView.activitiesReady ? toggleStandardHidden : null}
                    onEditActivity={projectView.activitiesReady ? (id) => {
                      const rec = stdActivity(id);
                      setActivityError('');
                      if (rec) setActivityForm(activityFormFrom(rec, projectView.warehouse && projectView.warehouse.ref, [projectView.projectId]));
                    } : null}
                    onRemoveActivity={projectView.activitiesReady ? removeStandardActivity : null}
                    activityRemoveLabel={(id) => removalLabel(stdActivity(id), 'standard')}
                    onEditDeparture={() => {
                      if (!refOriginDeparture.current) return;
                      refOriginDeparture.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
                      refOriginDeparture.current.focus();
                    }} />
                ) : (
                  <div className="helper-text">{projectViewError || 'Reading this project’s Run of Show…'}</div>
                )}
                {rosMessage && <div className="helper-text pp-error" role="alert">{rosMessage}</div>}
                {projectView && projectView.activitiesReady && (activityForm ? (
                  <ManualEventForm
                    value={activityForm}
                    onChange={setActivityForm}
                    onSave={saveStandardActivity}
                    onCancel={() => { setActivityForm(null); setActivityError(''); }}
                    members={[projectView.member]}
                    venueOptions={projectView.member && projectView.member.venueName ? [projectView.member] : []}
                    customLocations={[]}
                    savedLocations={savedLocations(projectView.ros.activities)}
                    warehouse={projectView.warehouse}
                    timeZones={projectView.timeZones || []}
                    single
                    saving={activityBusy}
                    error={activityError}
                  />
                ) : (
                  <div className="form-section">
                    <button type="button" className="pp-btn-tertiary" onClick={() => {
                      setActivityError('');
                      setActivityForm({ ...newActivityForm([projectView.projectId]), scope: 'chosen' });
                    }}>+ Add Activity</button>
                  </div>
                ))}
                <div className="form-row pp-origin-departure">
                  <div className="form-section form-section-half">
                    <label className="form-label" htmlFor="pp-origin-departure">Departure Time</label>
                    <input id="pp-origin-departure" ref={refOriginDeparture} type="text" className="input-field"
                      value={form.originDepartureTime}
                      onChange={(e) => setField('originDepartureTime', e.target.value)}
                      placeholder="Not set — e.g. 8:30 AM" />
                    <span className="helper-text">Time the crew leaves the warehouse.</span>
                  </div>
                </div>
              </RosSection>

              <RosSection title="Production Information" className="ros-packet-section">
                <SharedProductionSection
                  single
                  epsUrl={projectView && projectView.epsUrl}
                  production={projectView && projectView.production}
                  loading={projectViewLoading}
                  error={projectViewError}
                  deliveryApiUrl={deliveryApiUrl}
                  identity={packetIdentityFields(selectedProject)}
                  userEmail={userEmail}
                  memberIds={projectView ? [projectView.projectId] : []}
                  onApplied={() => loadProjectView(selectedProject)}
                />
              </RosSection>

              {/* Peers of Production Information, as in the Multi-Event builder. */}
              <div className="ros-packet-sections">
                <Collapse variant="section" title="Production Resources" summary={standardSummaries.resources}
                  open={openParts.resources} onToggle={() => toggleOpenPart('resources')}>
                  <StandardResources production={projectView && projectView.production} folderId={eps && eps.gdriveFolderId} />
                </Collapse>
                <div ref={refTrip}>
                <Collapse variant="section" title="Travel & Accommodations"
                  summary={memberships.length ? `${tripText(form)} · This project only` : tripText(form)}
                  attention={gateAttention(gateItems('trip'))} items={gateItems('trip')}
                  open={openParts.trip} onToggle={() => toggleOpenPart('trip')}>
                  {memberships.length > 0 && <GroupDetailsNote memberships={memberships} what="travel and accommodations" />}
                  <SharedDetailsForm part="trip" value={form} onChange={(next) => setForm((f) => ({ ...f, ...pickTravelTeam(next) }))} />
                </Collapse>
                </div>
                <div ref={refCrew}>
                  <Collapse variant="section" title="Delivery Team"
                    summary={memberships.length ? `${teamText(form)} · This project only` : teamText(form)}
                    attention={gateAttention(gateItems('team'))} items={gateItems('team')}
                    open={openParts.team} onToggle={() => toggleOpenPart('team')}>
                    {memberships.length > 0 && <GroupDetailsNote memberships={memberships} what="delivery team" />}
                    <SharedDetailsForm part="team" value={form} onChange={(next) => setForm((f) => ({ ...f, ...pickTravelTeam(next) }))} />
                  </Collapse>
                </div>
              </div>

              <RosSection title="Packet Readiness" className="ros-packet-section">
                <div ref={latestPacketRef} className="pp-final ros-readiness">
                  {stale && stale.isStale && (
                    <div className="pp-stale-note">
                      Details were saved after the last packet was generated — regenerate to include the latest changes.
                    </div>
                  )}
                  <div className="pp-final-row ros-generate-row">
                    <div className="pp-final-status ros-review">
                      {missingDriveFolder ? (
                        <span className="pp-status-warn">Add a Google Drive folder before generating</span>
                      ) : reviewItems.length === 0 && !gateBlocked ? (
                        <span className="pp-status-ready">✓ Ready to generate</span>
                      ) : reviewItems.length === 0 ? (
                        <span className="pp-status-warn">Complete the items below before generating</span>
                      ) : (
                        <>
                          <div className="ros-review-count">
                            {reviewItems.length} item{reviewItems.length !== 1 ? 's' : ''} need{reviewItems.length === 1 ? 's' : ''} review
                          </div>
                          <button type="button" className="me-action me-action--strong" aria-expanded={showReview}
                            onClick={() => setShowReview((v) => !v)}>
                            {showReview ? 'Hide items' : 'Review items →'}
                          </button>
                        </>
                      )}
                    </div>
                    <div className="pp-final-actions">
                      <button type="button" className="btn-calculate pp-btn-emphasis" onClick={handleGenerate} disabled={generating || missingDriveFolder || gateBlocked}>
                        {generating ? (
                          <>
                            <span className="spinner-small"></span>
                            <span style={{ marginLeft: 8 }}>Generating…</span>
                          </>
                        ) : (
                          <>{versionInfo.latestVersion > 0 ? 'Generate New Packet' : 'Generate Production Packet'} →</>
                        )}
                      </button>
                    </div>
                  </div>
                  <GenerateGate gate={gate} onGo={goToPart}
                    epsUrl={projectView && projectView.epsUrl} />
                  {showReview && reviewItems.length > 0 && (
                    <ul className="ros-issues">
                      {reviewItems.map((it) => (
                        <li key={it.text} className="ros-issue">
                          {it.text}
                          {it.go && <> <button type="button" className="pp-link-btn" onClick={it.go}>{it.goLabel}</button></>}
                        </li>
                      ))}
                    </ul>
                  )}
                  {generateStatus && (
                    <div className="helper-text" style={{ marginTop: 12 }}>{generateStatus}</div>
                  )}

                  {versionInfo.latestVersion > 0 ? (
                    <div className="pp-final-row ros-current-packet">
                      <div className="pp-final-info">
                        <span className="pp-eyebrow">Current Production Packet</span>
                        <div className="pp-final-when">
                          Version {versionInfo.latestVersion} · Generated {formatGeneratedAt(versionInfo.latestPacketGeneratedAt)}
                          {shortName(versionInfo.latestPacketGeneratedBy) ? ` · ${shortName(versionInfo.latestPacketGeneratedBy)}` : ''}
                        </div>
                        {eps && eps.gdriveFolderId && (
                          <a className="pp-prev-versions" href={`https://drive.google.com/drive/folders/${eps.gdriveFolderId}`}
                            target="_blank" rel="noopener noreferrer">Previous Versions →</a>
                        )}
                      </div>
                      <div className="pp-final-actions">
                        {versionInfo.latestPacketUrl && (
                          <a className="btn-secondary" style={{ textDecoration: 'none' }}
                            href={versionInfo.latestPacketUrl} target="_blank" rel="noopener noreferrer">View Packet</a>
                        )}
                        {driveDownloadUrl(versionInfo.latestPacketUrl) && (
                          <a className="btn-secondary" style={{ textDecoration: 'none' }}
                            href={driveDownloadUrl(versionInfo.latestPacketUrl)} target="_blank" rel="noopener noreferrer">Download PDF</a>
                        )}
                        {eps && eps.gdriveFolderId && (
                          <a className="btn-secondary" style={{ textDecoration: 'none' }}
                            href={`https://drive.google.com/drive/folders/${eps.gdriveFolderId}`}
                            target="_blank" rel="noopener noreferrer">Open in Drive</a>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="pp-final-empty">No packet generated yet.</div>
                  )}
                </div>
              </RosSection>
            </div>
          </PPSection>
        </>
      )}
    </div>
  );
}

/**
 * 01 — the first decision: one project, or several run together. Two quiet
 * options with a short explanation; the selected one is marked by weight and
 * a charcoal rule, not color.
 */
function PacketTypeSelector({ value, onChange }) {
  const options = [
    {
      value: 'standard',
      title: 'Standard Production Packet',
      text: 'One project with its production details.'
    },
    {
      value: 'multi',
      title: 'Multi-Event Production Packet + Run of Show',
      text: 'Multiple projects combined into one production packet and master Run of Show.'
    }
  ];
  return (
    <div className="me-type" role="radiogroup" aria-label="Packet type">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={`me-type-option${value === o.value ? ' me-type-option--on' : ''}`}
          onClick={() => onChange(o.value)}
        >
          <span className="me-type-title">{o.title}</span>
          <span className="me-type-text">{o.text}</span>
        </button>
      ))}
    </div>
  );
}

/** A completed step shown as one quiet line above the editor. */
/** A finished step, kept visible as quiet context; its action stays clickable. */
function StepSummary({ num, title, value, action }) {
  return (
    <div className="me-step me-step--done">
      <span className="pp-section-num">{num}</span>
      <span className="me-step-title">{title}</span>
      <span className="me-step-value">{value}</span>
      {action && <span className="me-step-action">{action}</span>}
    </div>
  );
}

/**
 * What the Standard page autosaves: the project's own departure time, its
 * travel and its team. Production Information (Venue Access, Site
 * Restrictions, Production Notes, the onsite contact) is saved field by field
 * by the same editor the Multi-Event packet uses, so it is not in this form
 * and an autosave never overwrites it.
 */
function emptyForm() {
  return {
    originDepartureTime: '',
    hotelName: '',
    numberOfRooms: '',
    hotelAddress: '',
    teamHotelConf: '',
    supervisor: '',
    deliveryPersonnel: [],
    overnightTravelRequired: '',
    teamStructure: ''
  };
}

function emptyVersion() {
  return {
    latestVersion: 0,
    latestPacketUrl: '',
    latestPacketGeneratedAt: '',
    latestPacketGeneratedBy: '',
    formLastSavedAt: '',
    formLastSavedBy: ''
  };
}

/** Numbered editorial section header + content. */
function PPSection({ num, title, action, children }) {
  return (
    <section className="pp-section">
      <div className="pp-section-head">
        <span className="pp-section-num">{num}</span>
        <h3 className="pp-section-title">{title}</h3>
        {action && <div className="pp-section-action">{action}</div>}
      </div>
      {children}
    </section>
  );
}

function AutosaveIndicator({ status }) {
  if (status === 'saving') return <span className="pp-autosave">Saving…</span>;
  if (status === 'saved') return <span className="pp-autosave pp-autosave--ok">✓ Changes saved automatically</span>;
  if (status === 'error') return <span className="pp-autosave pp-autosave--err">Autosave failed — your last change wasn't saved</span>;
  return null;
}

/** Derive a direct-download URL from a Drive file view/URL when possible. */
function driveDownloadUrl(url) {
  if (!url) return '';
  const m = url.match(/\/d\/([A-Za-z0-9_-]+)/) || url.match(/[?&]id=([A-Za-z0-9_-]+)/);
  return m ? `https://drive.google.com/uc?export=download&id=${m[1]}` : '';
}

/** "Jul 29, 2026, 1:38 PM CT" using the actual generated timestamp. */
function formatGeneratedAt(iso) {
  if (!iso) return '';
  return formatDisplayInstant(iso) || iso;
}

/** Compact display name from an email (rae@mayker.com → Rae) or a raw name. */
function shortName(who) {
  const raw = String(who || '').trim();
  if (!raw) return '';
  if (raw.indexOf('@') === -1) return raw;
  const first = raw.split('@')[0].split(/[._-]/)[0];
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : '';
}

/** "Pretend Engineering — ZZ TEST VENUE · Dec 7–8, 2027 · Project #990872" for the Project step. */
function standardProjectLine(eps, project) {
  const e = eps || {};
  const p = project || {};
  return [
    [e.clientName || p.clientName, e.venueName || p.venueName].filter(Boolean).join(' — '),
    e.projectDates || p.projectDates,
    e.projectNumber ? `Project #${e.projectNumber}` : ''
  ].filter(Boolean).join(' · ');
}

/** Only the travel and team fields leave the shared form for the Standard form. */
function pickTravelTeam(v) {
  const keys = ['hotelName', 'hotelAddress', 'teamHotelConf', 'numberOfRooms', 'supervisor', 'deliveryPersonnel',
    'overnightTravelRequired', 'teamStructure'];
  return keys.reduce((o, k) => (Object.prototype.hasOwnProperty.call(v, k) ? { ...o, [k]: v[k] } : o), {});
}

/**
 * A project in a Multi-Event packet: that packet prints its own group travel
 * and team (packet-level, not synced). Say so where these are edited, so the
 * two never read as one value.
 */
function GroupDetailsNote({ memberships, what }) {
  const titles = memberships.map((m) => `“${m.title}”`).join(', ');
  return (
    <p className="helper-text me-edit-readonly pp-group-note">
      Also in Multi-Event packet {titles}, which prints its own {what}. These print on this Standard packet only.
    </p>
  );
}


/**
 * The project's plans and resources, as the Multi-Event builder lists each
 * project's: the files themselves live in the project's Drive folder.
 */
function StandardResources({ production, folderId }) {
  const folder = folderId ? `https://drive.google.com/drive/folders/${folderId}` : '';
  if (!production) return <div className="helper-text">Reading this project’s resources…</div>;
  const list = (production.resources || []).filter((r) => !r.excluded);
  return (
    <div className="me-resources">
      {list.length === 0 && <div className="helper-text me-empty">No resources added</div>}
      {list.map((r) => (
        <div key={r.fileId} className="me-value-row">
          <div className="me-value-line">
            <a className="me-value" href={r.url} target="_blank" rel="noopener noreferrer">{r.label}</a>
          </div>
          <div className="me-meta">
            {/* A Standard packet prints every printable file after Resources & Processes (no companion PDFs). */}
            {[r.kindLabel, formatMB(r.bytes), r.mergeable ? 'Printed in the packet' : 'Not printable — open in Drive'].join(' · ')}
          </div>
        </div>
      ))}
      {folder && (
        <div className="me-value-line">
          <a className="me-action" href={folder} target="_blank" rel="noopener noreferrer">Add or change files in Drive</a>
        </div>
      )}
    </div>
  );
}

export default ProductionPackets;
